import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, UIHeading2, UIHeading4, useDeviceType } from '@cian/ui-kit';

import {
  isValuesValid,
  prepareInitialValues,
  prepareSubmitValues,
  prepareTrackingParams,
  useFormShowAnalytics,
  usePhoneFillAnalytics,
  useRequirementsFillAnalytics,
} from './utils';
import { trackCallbackClickAction } from '../../actions/analytics';
import { createConsultingRequestAction } from '../../actions/consultingRequest';
import { openRequestForm } from '../../actions/pageState';
import { setPhoneConfirmationError } from '../../actions/phoneValidationCode';
import { requestPhoneValidationCode } from '../../actions/phoneValidationCode/requestPhoneValidationCode';
import {
  RequestFormModalContent,
  RequestFormModalFooter,
  RequestFormModalHeader,
  RequestFormModalWindow,
  SuccessModalWindow,
} from '../../components/RequestFormModal';

import { useHhQueryParamsFromCookie } from '../../hooks';
import { selectCityAds } from '../../selectors/cpa';
import { selectLocation } from '../../selectors/location';
import { selectIsSubmitting, selectPageState } from '../../selectors/pageState';
import { selectPhoneConfirmationErrorText } from '../../selectors/phoneConfirmation/selectPhoneConfirmationErrorText';
import { selectUserPhone, selectUserPhoneConfirmed } from '../../selectors/user';
import { getPopularRegions } from '../../services/popularRegions';
import { searchRegionSuggests } from '../../services/regionSuggests';
import { EPageState } from '../../types/pageState';
import { TThunkDispatch } from '../../types/redux/thunk';
import { IRegion, IRequestFormValues } from '../../types/requestForm';
import { useApplicationContext } from '../../utils/applicationContext';
import { PhoneConfirmationContainer } from '../PhoneConfirmation';
import { RequestFormModalPersonalAgreementContainer } from '../RequestFormModalPersonalAgreement';

const INITIAL_VALUES: IRequestFormValues = {
  phone: '',
  region: null,
  requirements: '',
};

export interface IRequestFormModalWindowContainerProps {
  open: boolean;
  onClose: () => void;
}

export const RequestFormModalWindowContainer: React.FC<IRequestFormModalWindowContainerProps> = ({ open, onClose }) => {
  const [values, setValues] = React.useState<IRequestFormValues>(INITIAL_VALUES);
  const [isValid, setIsValid] = React.useState(false);

  const popularRegionsRef = React.useRef<IRegion[] | null>(null);

  const isSubmitting = useSelector(selectIsSubmitting);
  const location = useSelector(selectLocation);
  const cityAds = useSelector(selectCityAds);
  const userPhone = useSelector(selectUserPhone);
  const userPhoneConfirmed = useSelector(selectUserPhoneConfirmed);
  const pageState = useSelector(selectPageState);
  const dispatch: TThunkDispatch = useDispatch();
  const context = useApplicationContext();
  const cookieHhValue = useHhQueryParamsFromCookie();
  const deviceType = useDeviceType();
  const errorText = useSelector(selectPhoneConfirmationErrorText);

  const isPhone = deviceType === 'phone';

  const header = isPhone ? <UIHeading4>Расскажите, что вы ищете</UIHeading4> : <RequestFormModalHeader />;

  useFormShowAnalytics(open);

  const analyticsPhoneChangeHandler = usePhoneFillAnalytics();
  const analyticsRequirementsChangeHandler = useRequirementsFillAnalytics();

  const handleRequirementsChange = React.useCallback(() => {
    analyticsRequirementsChangeHandler(values.phone);
  }, [analyticsRequirementsChangeHandler, values.phone]);

  const updateValues = React.useCallback(
    (nextValues: Partial<IRequestFormValues>) => {
      dispatch(setPhoneConfirmationError({ phoneError: '', timeError: '' }));
      setValues(prev => ({ ...prev, ...nextValues }));
    },
    [dispatch],
  );

  const searchRegion = React.useCallback(
    async (text: string): Promise<IRegion[]> => {
      if (text.length >= 3) {
        return searchRegionSuggests(context, { text });
      }

      if (!popularRegionsRef.current) {
        popularRegionsRef.current = await getPopularRegions(context);
      }

      return popularRegionsRef.current;
    },
    [context],
  );

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (isSubmitting) {
        return;
      }

      try {
        // TODO: Параметры не из формы лучше перенести и формировать в thunk-е.
        const preparedValues = prepareSubmitValues(values, cookieHhValue || undefined, cityAds);
        if (userPhoneConfirmed) {
          await dispatch(createConsultingRequestAction(preparedValues));
        } else {
          dispatch(requestPhoneValidationCode({ phone: preparedValues.phone }));
        }
      } catch {
        // ignore exception
      }

      dispatch(trackCallbackClickAction(prepareTrackingParams(values)));
    },
    [isSubmitting, dispatch, values, userPhoneConfirmed, cookieHhValue, cityAds],
  );

  const handleSubmitWithCode = React.useCallback(
    async (validationCode: string | null) => {
      if (isSubmitting) {
        return;
      }

      try {
        // TODO: Параметры не из формы лучше перенести и формировать в thunk-е.
        const preparedValues = prepareSubmitValues(values, cookieHhValue || undefined, cityAds);
        await dispatch(createConsultingRequestAction({ ...preparedValues, validationCode }));
      } catch {
        // ignore exception
      }
    },
    [isSubmitting, dispatch, values, cookieHhValue, cityAds],
  );

  const handleSuccessModalClosing = React.useCallback(() => {
    dispatch(openRequestForm());
    setValues(INITIAL_VALUES);
    updateValues(prepareInitialValues({ location, userPhone }));
    onClose();
  }, [dispatch, onClose, updateValues, location, userPhone]);

  React.useEffect(() => {
    updateValues(prepareInitialValues({ location, userPhone }));
  }, [updateValues, location, userPhone]);

  React.useEffect(() => {
    setIsValid(isValuesValid(values));
  }, [values]);

  if (pageState === EPageState.RequestSent) {
    return <SuccessModalWindow onClose={handleSuccessModalClosing} open={open} />;
  }

  if (pageState === EPageState.ValidationCode) {
    return (
      <RequestFormModalWindow
        header={isPhone ? <UIHeading4>Введите код из смс</UIHeading4> : <UIHeading2>Введите код из смс</UIHeading2>}
        content={
          <PhoneConfirmationContainer
            phone={values.phone}
            isSubmitting={isSubmitting}
            handleSubmit={handleSubmitWithCode}
          />
        }
        onClose={onClose}
        open={open}
      />
    );
  }

  return (
    <RequestFormModalWindow
      header={header}
      content={
        <RequestFormModalContent
          onChange={updateValues}
          onPhoneChange={analyticsPhoneChangeHandler}
          onRegionSearch={searchRegion}
          onRequirementsChange={handleRequirementsChange}
          submitting={isSubmitting}
          values={values}
          phoneError={errorText}
        />
      }
      footer={
        <RequestFormModalFooter
          personalAgreement={<RequestFormModalPersonalAgreementContainer />}
          submitButton={
            <Button disabled={!isValid} onClick={handleSubmit} loading={isSubmitting} fullWidth>
              Заказать звонок
            </Button>
          }
        />
      }
      onClose={onClose}
      open={open}
    />
  );
};
